import type { RootLocationConfiguration } from '@cognite/apm-config';

import { useIsLocationEnabledContext } from './use-is-location-enabled-for-feature-flag.context';
import {
  isAllLocationsEnabled,
  isCurrentLocationEnabled,
  validateLocationsArray,
} from './utils';

/**
 * Checks if the current selected root location is enabled for the given feature flag.
 * The flag itself also needs to be enabled for this to return true.
 *
 * @param flagKey The Unleash feature flag key.
 * @param selectedRootLocation The currently selected root location configuration.
 * @returns True if the current location is enabled, false otherwise.
 */
export const useIsLocationEnabledForFeatureFlag = (
  flagKey: string,
  selectedRootLocation?: RootLocationConfiguration
): {
  isEnabled: boolean;
  isLoading: boolean;
} => {
  const { useFlag, useVariant, useFlagsStatus, captureException } =
    useIsLocationEnabledContext();
  const enabledLocations = useVariant(flagKey).payload?.value ?? '';
  const isFlagEnabled = useFlag(flagKey);
  const { flagsReady } = useFlagsStatus();

  const isLoading = !flagsReady;

  // For the new infield config version we use external id. The old version uses asset external id.
  const locationExternalId =
    selectedRootLocation?.externalId ?? selectedRootLocation?.assetExternalId;

  if (!locationExternalId || !enabledLocations || !isFlagEnabled) {
    return { isEnabled: false, isLoading };
  }

  try {
    const parsedLocations: string[] = JSON.parse(enabledLocations);

    validateLocationsArray(parsedLocations);

    return {
      isEnabled:
        isAllLocationsEnabled(parsedLocations) ||
        isCurrentLocationEnabled(parsedLocations, locationExternalId),
      isLoading,
    };
  } catch (error) {
    captureException(error);
    return { isEnabled: false, isLoading };
  }
};
